import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import RadioGroup from 'ext-ui/components/RadioGroup/RadioGroup'
import AiStarsIcon from 'ext-ui/svg/icon/AiStarsIcon'
import AttachmentFileIcon from 'ext-ui/svg/icon/AttachmentFileIcon'
import CheckCircleFilledIcon from 'ext-ui/svg/icon/CheckCircleFilledIcon'
import React, { useEffect, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import {
  ReceiptRecoveryCase,
  ReceiptRecoveryLifecycleState,
} from 'src/models/transaction.models'

import { getReceiptRecoveryProvenance } from '../receiptRecoveryProvenance'
import style, {
  receiptRecoveryHeaderStyle,
} from './ReceiptRecoverySection.style'

export type ReceiptRecoveryReviewOutcome = 'MATCHES' | 'DOES_NOT_MATCH'

interface ReceiptRecoverySectionProps {
  receiptRecoveryCase: ReceiptRecoveryCase
  renderUploader: (isMissing: boolean) => React.ReactNode
  headerSupplement?: React.ReactNode
  headerAction?: React.ReactNode
  isMockPreview?: boolean
  onSubmitReview?: (outcome: ReceiptRecoveryReviewOutcome) => void
}

function formatTimestamp(timestamp?: string): string | undefined {
  if (!timestamp) return undefined

  const date = new Date(timestamp)
  if (Number.isNaN(date.valueOf())) return undefined

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

const ReceiptRecoverySection: React.FC<ReceiptRecoverySectionProps> = ({
  receiptRecoveryCase,
  renderUploader,
  headerSupplement,
  headerAction,
  isMockPreview = false,
  onSubmitReview,
}): React.ReactElement => {
  const translate = useTranslate()
  const [selectedReview, setSelectedReview] =
    useState<ReceiptRecoveryReviewOutcome | null>(null)

  const { lifecycleState } = receiptRecoveryCase
  const lastAgentRunAt = formatTimestamp(receiptRecoveryCase.lastAgentRunAt)
  const reconciledAt = formatTimestamp(receiptRecoveryCase.reconciledAt)
  const recoveredReceiptProvenance = getReceiptRecoveryProvenance(
    receiptRecoveryCase.recoveredFrom,
  )

  useEffect(() => {
    setSelectedReview(null)
  }, [lifecycleState, receiptRecoveryCase.recoveredAttachmentId])

  const renderAgentContext = (
    provenance: React.ReactNode,
    description: React.ReactNode,
  ): React.ReactElement => (
    <div className={'ReceiptRecoverySection--agent-context'}>
      <div className={'ReceiptRecoverySection--agent-label'}>
        <AiStarsIcon width={20} height={20} />
        <span>{translate('Receipt Agent')}</span>
      </div>
      <p className={'ReceiptRecoverySection--provenance'}>{provenance}</p>
      <p className={'ReceiptRecoverySection--description'}>{description}</p>
    </div>
  )

  const renderRecoveredReceipt = (): React.ReactElement => (
    <div className={'ReceiptRecoverySection--receipt'}>
      <AttachmentFileIcon className={'ReceiptRecoverySection--receipt-icon'} />
      <span className={'ReceiptRecoverySection--receipt-name'}>
        {receiptRecoveryCase.recoveredFileName ??
          translate('Recovered receipt')}
      </span>
    </div>
  )

  const renderWaitingOnSpender = (): React.ReactElement => (
    <>
      {renderAgentContext(
        lastAgentRunAt
          ? translate('Receipt Agent · last checked ${date}', {
              date: lastAgentRunAt,
            })
          : translate('Receipt Agent · waiting for the spender'),
        translate(
          'No receipt is attached to this transaction yet. Extend asked the spender for one and is still waiting — there is nothing to review.',
        ),
      )}
      {renderUploader(false)}
    </>
  )

  const renderMissingReceipt = (): React.ReactElement => (
    <>
      {renderAgentContext(
        lastAgentRunAt
          ? translate(
              'Receipt Agent · searched connected sources · last run ${date}',
              {
                date: lastAgentRunAt,
              },
            )
          : translate('Receipt Agent · searched connected sources'),
        translate(
          'The Receipt Agent searched every connected source available to it and found no receipt for this charge. The spender has not attached one either, so there is no document to review. Someone has to upload it.',
        ),
      )}
      {renderUploader(true)}
    </>
  )

  const renderNeedsReview = (): React.ReactElement => (
    <>
      {renderAgentContext(
        translate(recoveredReceiptProvenance.label),
        translate(recoveredReceiptProvenance.description),
      )}
      {renderRecoveredReceipt()}
      <div className={'ReceiptRecoverySection--review'}>
        <RadioGroup<ReceiptRecoveryReviewOutcome>
          name={'receipt-recovery-review'}
          label={translate('Does this receipt cover this transaction?')}
          value={selectedReview}
          onChange={(review) => setSelectedReview(review)}
          options={[
            {
              value: 'MATCHES',
              title: translate('Receipt matches this transaction'),
              description: translate(
                'Confirm the recovered receipt as evidence for this charge.',
              ),
            },
            {
              value: 'DOES_NOT_MATCH',
              title: translate('Receipt does not match'),
              description: translate(
                'Return this charge to the Receipt Agent for follow-up.',
              ),
            },
          ]}
        />
        <Button
          className={'ReceiptRecoverySection--submit'}
          disabled={
            selectedReview === null ||
            (!isMockPreview && onSubmitReview === undefined)
          }
          onClick={() => selectedReview && onSubmitReview?.(selectedReview)}
        >
          {translate('Submit review')}
        </Button>
      </div>
      {renderUploader(false)}
    </>
  )

  const renderReconciled = (): React.ReactElement => (
    <>
      {renderRecoveredReceipt()}
      <div
        className={'ReceiptRecoverySection--reconciled'}
        role={'status'}
        aria-label={translate('Receipt matches this transaction')}
      >
        <div className={'ReceiptRecoverySection--reconciled-heading'}>
          <CheckCircleFilledIcon />
          <span>{translate('Receipt matches this transaction')}</span>
        </div>
        <p className={'ReceiptRecoverySection--confirmation'}>
          {translate('Confirmed by ${name}${date} · recorded in Activity.', {
            name:
              receiptRecoveryCase.reconciledByDisplayName ??
              translate('a reviewer'),
            date: reconciledAt ? ` on ${reconciledAt}` : '',
          })}
        </p>
      </div>
    </>
  )

  const contentByLifecycleState: Record<
    ReceiptRecoveryLifecycleState,
    React.ReactElement
  > = {
    [ReceiptRecoveryLifecycleState.WaitingOnSpender]: renderWaitingOnSpender(),
    [ReceiptRecoveryLifecycleState.MissingReceipt]: renderMissingReceipt(),
    [ReceiptRecoveryLifecycleState.NeedsReview]: renderNeedsReview(),
    [ReceiptRecoveryLifecycleState.ReceiptReconciled]: renderReconciled(),
  }

  const lifecycleLabel: Record<ReceiptRecoveryLifecycleState, string> = {
    [ReceiptRecoveryLifecycleState.WaitingOnSpender]:
      translate('Waiting on Spender'),
    [ReceiptRecoveryLifecycleState.MissingReceipt]:
      translate('Missing Receipt'),
    [ReceiptRecoveryLifecycleState.NeedsReview]: translate('Needs Review'),
    [ReceiptRecoveryLifecycleState.ReceiptReconciled]:
      translate('Receipt Reconciled'),
  }

  const lifecyclePillColor: Record<
    ReceiptRecoveryLifecycleState,
    'highlight' | 'severe' | 'new' | undefined
  > = {
    [ReceiptRecoveryLifecycleState.WaitingOnSpender]: undefined,
    [ReceiptRecoveryLifecycleState.MissingReceipt]: 'severe',
    [ReceiptRecoveryLifecycleState.NeedsReview]: 'new',
    [ReceiptRecoveryLifecycleState.ReceiptReconciled]: 'highlight',
  }

  return (
    <div css={style} className={'ReceiptRecoverySection'}>
      <div css={receiptRecoveryHeaderStyle}>
        <div className={'ReceiptRecoverySection--header-label'}>
          <span className={'font-h3'}>{translate('Receipts')}</span>
          <Pill size={'small'} color={lifecyclePillColor[lifecycleState]}>
            {lifecycleLabel[lifecycleState]}
          </Pill>
          {headerSupplement}
        </div>
        {headerAction}
      </div>
      {contentByLifecycleState[lifecycleState]}
    </div>
  )
}

export default ReceiptRecoverySection
